import { Box, Skeleton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import dayjs from 'dayjs';
import React, { useMemo, useRef } from 'react';
import type { LabelProps, XAxisProps, YAxisProps } from 'recharts';
import { ResponsiveContainer } from 'recharts';
import { DurationUnit } from 'switch-shared/utils/duration.enum';

import { formatNumber, graphFileName } from '../../../utils/formatting';
import AxisTickWithSubLabel from './AxisTickWithSubLabel';
import ExportMenu from './ExportMenu';
import { getChartTypography } from './utils/layout';
import type { LegendExportConfig } from './utils/svgLegend';
import { maxTextWidth } from './utils/textWidth';
import {
  computeTimeAxisTicks,
  computeTimeDomain,
  createBucketAwareTooltipXFormatter,
  createBucketAwareXFormatter,
} from './utils/timeAxis';

type Props = {
  children: React.ReactNode;
  height?: number | string;
  title?: string;
  headerContent?: React.ReactNode;
  action?: React.ReactNode;

  fileName?: string;
  series?: { name: string; data: (number | null)[] }[];
  categories?: Array<string | number | Date>;

  hideExport?: boolean;
  /**
   * Optional override; if omitted and series+categories provided, wrapper will build CSV
   */
  getCsv?: () => string | undefined;
  /**
   * Optional override; if omitted and series+categories provided, wrapper will build Excel rows
   */
  getExcelRows?: () => Array<Array<string | number>> | undefined;
  /**
   * Optional override; defaults to largest recharts surface
   */
  getSvg?: () => SVGSVGElement | null;
  /**
   * Controls container aspect ratio
   */
  aspectRatio?: 'rectangle' | 'none';
  /**
   * Optional override; if omitted and series+categories provided, wrapper will build CSV
   */
  getLegendExportConfig?: () => LegendExportConfig | undefined;
  /**
   * Show loading state
   */
  isLoading?: boolean;
};

export default function ReChartContainer({
  children,
  height = '100%',
  title,
  headerContent,
  action,
  fileName,
  hideExport = false,
  getCsv,
  getExcelRows,
  series,
  categories,
  getSvg,
  aspectRatio = 'none',
  getLegendExportConfig,
  isLoading = false,
}: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);

  function findChartSvg() {
    if (getSvg) return getSvg();
    if (!containerRef.current) return null as SVGSVGElement | null;
    const svgs = Array.from(
      containerRef.current.querySelectorAll('svg.recharts-surface')
    ) as SVGSVGElement[];
    if (svgs.length === 0) return null;
    let best: SVGSVGElement | null = null;
    let bestArea = -1;
    for (const s of svgs) {
      const rect = s.getBoundingClientRect();
      const area = rect.width * rect.height;
      if (area > bestArea) {
        best = s;
        bestArea = area;
      }
    }
    return best;
  }

  function computeFileBaseName() {
    const normalizeForFileName = (value: string | number | Date) => {
      const d = dayjs(value);
      return d.isValid() ? d.toDate() : String(value);
    };

    return (
      fileName ??
      graphFileName(
        title ?? 'chart',
        categories && categories.length > 0
          ? normalizeForFileName(categories[0])
          : new Date(),
        categories && categories.length > 0
          ? normalizeForFileName(categories[categories.length - 1])
          : new Date()
      )
    );
  }

  function getRowsFromSeries() {
    if (!series || !categories) return undefined;
    const headers = [
      getFirstColumnHeader(categories),
      ...series.map((s) => s.name),
    ];
    const rows: Array<Array<string | number>> = [headers];
    for (let i = 0; i < (categories?.length ?? 0); i++) {
      const raw = categories![i];
      const ts = formatCategory(raw);
      const values = series!.map((s) => {
        const v = s.data[i];
        return v == null ? '' : v;
      });
      rows.push([ts, ...values]);
    }
    return rows;
  }

  function getCsvFromSeries() {
    const rows = getRowsFromSeries();
    if (!rows) return undefined;
    return rows.map((r) => r.join(',')).join('\n');
  }

  function formatCategory(value: string | number | Date) {
    const d = dayjs(value);
    if (d.isValid()) return d.format('DD-MM-YYYY HH:mm:ss');
    return String(value);
  }

  function getFirstColumnHeader(cats: Array<string | number | Date>) {
    const allDateLike = cats.every((c) => dayjs(c).isValid());
    return allDateLike ? 'Timestamp' : 'Category';
  }

  const getContainerStyle = () => {
    const baseStyle: React.CSSProperties = {
      position: 'relative',
      width: '100%',
      height,
    };

    if (aspectRatio === 'rectangle') {
      return {
        ...baseStyle,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      };
    }

    return baseStyle;
  };

  const getInnerStyle = () => {
    if (aspectRatio === 'rectangle') {
      // Create a rectangle container by constraining to the smaller dimension
      return {
        position: 'relative' as const,
        width: 'auto',
        height: '100%',
        aspectRatio: '2 / 1',
        maxWidth: '100%',
      };
    }
    return { position: 'relative' as const, width: '100%', height: '100%' };
  };

  return (
    <Box
      ref={containerRef as any}
      sx={{
        ...getContainerStyle(),
        '& .recharts-wrapper:focus:not(:focus-visible), & .recharts-wrapper :focus:not(:focus-visible)':
          {
            outline: 'none',
          },
      }}
    >
      <Box
        sx={{
          ...getInnerStyle(),
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
        }}
      >
        {!isLoading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              zIndex: 2,
              display: 'flex',
              pointerEvents: 'none',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 1,
              py: 1,
              gap: 1,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                pointerEvents: 'auto',
              }}
            >
              {headerContent}
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                pointerEvents: 'auto',
              }}
            >
              {action}
              {!hideExport && (
                <ExportMenu
                  getSvg={findChartSvg}
                  computeFileBaseName={computeFileBaseName}
                  getCsv={getCsv ?? getCsvFromSeries}
                  getExcelRows={getExcelRows ?? getRowsFromSeries}
                  getLegendExportConfig={getLegendExportConfig}
                />
              )}
            </Box>
          </Box>
        )}

        {isLoading ? (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              p: 1,
              width: '100%',
              height: '100%',
              minHeight: 0,
            }}
          >
            <Skeleton
              variant='text'
              width='30%'
              height={34}
              sx={{ mx: 'auto', borderRadius: '8px' }}
            />
            <Skeleton
              variant='rectangular'
              width='100%'
              height='100%'
              sx={{ borderRadius: '8px' }}
            />
          </Box>
        ) : (
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <ResponsiveContainer width={'100%'} height={'100%'} debounce={400}>
              {children as React.ReactElement}
            </ResponsiveContainer>
          </Box>
        )}
      </Box>
    </Box>
  );
}

// Axis titles helper
type AxisTitleOptions = {
  xTitle?: string;
  yTitle?: string;
  color?: string;
  /**
   * Offset for x-axis title
   * @default -18
   */
  xOffset?: number;
  /**
   * Offset for y-axis title
   * @default -10
   */
  yOffset?: number;
};

function useAxisTitleLabels(options: AxisTitleOptions) {
  const theme = useTheme();
  const { axisFontSize, fontFamily } = getChartTypography(theme);

  const xTitle = options?.xTitle;
  const yTitle = options?.yTitle;
  const color = options?.color ?? theme.palette.text.secondary;
  const xOffset = options?.xOffset ?? -18;
  const yOffset = options?.yOffset ?? -10;

  const xAxisTitle = useMemo(
    () =>
      xTitle
        ? ({
            value: xTitle,
            position: 'insideBottom',
            fontFamily,
            fontSize: axisFontSize,
            fontWeight: 600,
            fill: color,
            offset: xOffset,
          } as LabelProps)
        : undefined,
    [xTitle, fontFamily, axisFontSize, color, xOffset]
  );

  const yAxisTitle = useMemo(
    () =>
      yTitle
        ? ({
            value: yTitle,
            position: 'insideLeft',
            fontFamily,
            fontSize: axisFontSize,
            angle: -90,
            fontWeight: 600,
            fill: color,
            offset: yOffset,
            style: { textAnchor: 'middle' },
          } as LabelProps)
        : undefined,
    [yTitle, fontFamily, axisFontSize, color, yOffset]
  );

  return { xAxisTitle, yAxisTitle } as const;
}

// Inline SVG chart titles (title + optional subtitle)
export function ChartTitles({
  title,
  subtitle,
}: {
  title?: string;
  subtitle?: string;
}) {
  const theme = useTheme();
  const {
    titleFontFamily,
    titleFontSize,
    subtitleFontFamily,
    subtitleFontSize,
  } = getChartTypography(theme);

  return (
    <>
      {title && (
        <text
          y={titleFontSize + 4}
          x={10}
          fill={theme.palette.text.primary}
          style={{
            fontFamily: titleFontFamily,
            fontSize: titleFontSize,
            fontWeight: 500,
          }}
        >
          {title}
        </text>
      )}
      {subtitle && (
        <text
          y={(title ? titleFontSize + 12 : 0) + subtitleFontSize}
          x={10}
          fill={theme.palette.text.secondary}
          style={{
            fontFamily: subtitleFontFamily,
            fontSize: subtitleFontSize,
            fontWeight: 500,
          }}
        >
          {subtitle}
        </text>
      )}
    </>
  );
}

// Comprehensive axis configuration hook
export type AxisSharedPropsOptions = {
  data: Array<{ timestamp: string | number; ts: number; [key: string]: any }>;
  xTitle?: string;
  yTitle?: string;
  bucketOptions?: { size?: number; unit: DurationUnit };
  xFormatter?: (value: string | number) => string;
  yFormatter?: (value: number) => string;
  tooltipXFormatter?: (value: string | number) => string;
  rotateXTicks?: boolean;
  /**
   * X-axis type override. Defaults to 'number' with scale='time' when data has timestamps
   * @default 'number'
   */
  xAxisType?: 'number' | 'category';
  /**
   * X-axis interval. Defaults to 'preserveStartEnd' for time-based, 'equidistantPreserveStart' for category
   * @default 'preserveStartEnd'
   */
  xAxisInterval?: 'preserveStartEnd' | 'equidistantPreserveStart' | number;
  /**
   * Minimum Y-axis value to display. When provided, the Y-axis domain will be [yMin, 'auto'].
   */
  yMin?: number;
  /**
   * Optional timeframe dates for automatic timeframe-based formatting.
   * When provided, formatting will be based on total timeframe duration instead of bucket unit.
   * - Timeframe > 1 day: formats as DD/MM
   * - Timeframe <= 1 day: formats as HH:mm
   */
  timeframeOptions?: { startDate: Date | string; endDate: Date | string };
  /**
   * Number of ticks to sample for Y-axis width calculation
   * @default 5
   */
  tickCount?: number;

  /** Second line under an x tick, looked up by its raw value; ignored when rotateXTicks is set. */
  xTickSubLabel?: (value: string | number) => string | null | undefined;
};

export function useAxisSharedProps(options: AxisSharedPropsOptions) {
  const theme = useTheme();
  const { axisFontSize, fontFamily } = getChartTypography(theme);

  const {
    data,
    xTitle,
    yTitle,
    bucketOptions,
    xFormatter,
    yFormatter,
    tooltipXFormatter,
    rotateXTicks = false,
    xAxisType = 'number',
    xAxisInterval = 'equidistantPreserveStart',
    timeframeOptions,
    tickCount = 5,
    xTickSubLabel,
  } = options;

  // Filter ticks to only show those aligned with bucket intervals
  const timeBase = useMemo(
    () =>
      data.map((d) => ({
        timestamp: d.timestamp,
        ts: d.ts,
      })),
    [data]
  );

  const filteredTicks = useMemo(
    () => computeTimeAxisTicks(timeBase, bucketOptions),
    [timeBase, bucketOptions]
  );

  const domain = useMemo(
    () => computeTimeDomain(timeBase, bucketOptions),
    [timeBase, bucketOptions]
  );

  // Formatters
  const defaultXFormatter = useMemo(
    () =>
      createBucketAwareXFormatter(
        bucketOptions,
        xFormatter,
        timeframeOptions,
        rotateXTicks ? 11 : undefined
      ),
    [bucketOptions, xFormatter, timeframeOptions, rotateXTicks]
  );

  const defaultTooltipXFormatter = useMemo(
    () =>
      createBucketAwareTooltipXFormatter(
        bucketOptions,
        tooltipXFormatter,
        timeframeOptions
      ),
    [bucketOptions, tooltipXFormatter, timeframeOptions]
  );

  const defaultYFormatter = useMemo(
    () =>
      yFormatter ??
      ((v: number) => {
        return formatNumber(Number(v));
      }),
    [yFormatter]
  );

  // Calculate Y-axis width dynamically based on tick label text width
  // Uses SVG-based measurement for pixel-perfect accuracy matching Recharts rendering
  const yCandidates = useMemo(() => {
    if (data.length === 0) return [];

    let vals: number[] = [];

    // Otherwise, collect all numeric values from all fields (except timestamp/ts)
    const allValues: number[] = [];
    data.forEach((d) => {
      Object.entries(d).forEach(([key, value]) => {
        if (
          key !== 'timestamp' &&
          key !== 'ts' &&
          value != null &&
          !isNaN(Number(value))
        ) {
          allValues.push(Number(value));
        }
      });
    });
    vals = allValues;

    if (vals.length === 0) return [];

    const min = vals.reduce(
      (minimum, value) => Math.min(minimum, value),
      Infinity
    );
    const max = vals.reduce(
      (maximum, value) => Math.max(maximum, value),
      -Infinity
    );
    const samples = [min, max];

    if (tickCount && tickCount > 2) {
      for (let i = 1; i < tickCount - 1; i++) {
        const t = min + ((max - min) * i) / (tickCount - 1);
        samples.push(t);
      }
    }

    return samples.map((v) =>
      defaultYFormatter ? defaultYFormatter(v) : String(v)
    );
  }, [data, defaultYFormatter, tickCount]);

  const yDomainCollapsed = useMemo(() => {
    const yMin = options?.yMin ?? 0;
    for (const d of data) {
      for (const [key, value] of Object.entries(d)) {
        if (
          key !== 'timestamp' &&
          key !== 'ts' &&
          value != null &&
          !isNaN(Number(value)) &&
          Number(value) !== yMin
        ) {
          return false;
        }
      }
    }
    return true;
  }, [data, options?.yMin]);

  const yTickWidth = useMemo(
    () =>
      yCandidates.length > 0
        ? maxTextWidth(yCandidates, axisFontSize, fontFamily)
        : 0,
    [yCandidates, axisFontSize, fontFamily]
  );

  const yGap = 8; // Breathing room near the axis line
  // const titleSpace = yTitle ? 28 : 0; // Space for rotated Y-axis title
  const yAxisWidth = yTickWidth > 0 ? yTickWidth + yGap : yTitle ? 60 : 35;

  // Calculate negative offset to push title left away from ticks
  // const dynamicYOffset = yTickWidth > 0 ? -(yTickWidth / 2 + 4) : -10;

  // Axis title labels with negative offset for insideLeft positioning
  const { xAxisTitle, yAxisTitle } = useAxisTitleLabels({
    xTitle,
    yTitle,
    // yOffset: dynamicYOffset,
  });

  // Determine if this is time-series data (has bucketOptions) or categorical
  const isTimeAxis = !!bucketOptions;

  // X-axis configuration
  const xAxisProps = useMemo<XAxisProps>(() => {
    const showSubLabels = xTickSubLabel != null && !rotateXTicks;

    return {
      type: xAxisType,
      dataKey: isTimeAxis ? 'ts' : 'timestamp',
      scale: xAxisType === 'number' ? 'time' : undefined,
      domain: isTimeAxis ? domain : undefined,
      ticks: isTimeAxis ? filteredTicks : undefined,
      interval: xAxisInterval,
      minTickGap: 34,
      stroke: theme.palette.divider,
      height: rotateXTicks
        ? axisFontSize + 68
        : axisFontSize + 24 + (showSubLabels ? axisFontSize + 6 : 0),
      tickMargin: rotateXTicks ? 5 : 10,
      tick: showSubLabels
        ? (props: any) => (
            <AxisTickWithSubLabel
              {...props}
              formatter={defaultXFormatter}
              subLabel={xTickSubLabel}
              fontFamily={fontFamily}
              fontSize={axisFontSize}
              fill={theme.palette.text.secondary}
              subFill={theme.palette.text.primary}
            />
          )
        : {
            fontFamily,
            fontSize: axisFontSize,
            fill: theme.palette.text.secondary,
            textAnchor: rotateXTicks ? 'end' : 'middle',
          },
      angle: rotateXTicks ? -35 : 0,
      tickFormatter: defaultXFormatter,
      label: xAxisTitle,
    };
  }, [
    xAxisType,
    isTimeAxis,
    domain,
    filteredTicks,
    xAxisInterval,
    rotateXTicks,
    axisFontSize,
    theme.palette.divider,
    theme.palette.text.secondary,
    theme.palette.text.primary,
    fontFamily,
    defaultXFormatter,
    xAxisTitle,
    xTickSubLabel,
  ]);

  // Y-axis configuration
  const yAxisProps = useMemo<YAxisProps>(
    () => ({
      tick: {
        fontFamily,
        fontSize: axisFontSize,
        fill: theme.palette.text.secondary,
      },
      stroke: 'transparent',
      tickFormatter: defaultYFormatter,
      width: yAxisWidth,
      tickLine: false,
      label: yAxisTitle,
      domain: yDomainCollapsed
        ? [options?.yMin ?? 0, (options?.yMin ?? 0) + 1]
        : [options?.yMin ?? 0, 'auto'],
      allowDataOverflow: yDomainCollapsed,
      ticks: yDomainCollapsed ? [options?.yMin ?? 0] : undefined,
    }),
    [
      fontFamily,
      axisFontSize,
      theme.palette.text.secondary,
      defaultYFormatter,
      yAxisWidth,
      yAxisTitle,
      options?.yMin,
      yDomainCollapsed,
    ]
  );

  return {
    xAxisProps,
    yAxisProps,
    xAxisTitle,
    yAxisTitle,
    filteredTicks,
    domain,
    defaultXFormatter,
    defaultTooltipXFormatter,
    defaultYFormatter,
  } as const;
}
